import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { Search, X, Loader } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import productService from '../services/productService';

const SearchContainer = styled.div`
  position: relative;
  width: 48px;
  transition: all 0.3s ease;
  flex-shrink: 0;
  
  ${props => props.$isExpanded ? `
    width: 400px;
    flex-shrink: 0;
    
    @media (max-width: 768px) {
      width: 350px;
    }
    
    @media (max-width: 480px) {
      width: calc(100vw - 4rem);
      max-width: 300px;
    }
  ` : `
    width: 48px;
  `}
`;

const CompactSearchButton = styled.button`
  background: transparent;
  border: none;
  border-radius: 50%;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: scale(1.05);
  }
  
  svg {
    color: white;
  }
  
  @media (max-width: 768px) {
    width: 44px;
    height: 44px;
  }
  
  @media (max-width: 480px) {
    width: 40px;
    height: 40px;
  }
`;

const SearchInputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 25px;
  padding: 0.5rem 1rem;
  transition: all 0.3s ease;
  
  &:focus-within {
    background: rgba(255, 255, 255, 0.15);
    border-color: rgba(255, 255, 255, 0.4);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
  
  @media (max-width: 768px) {
    padding: 0.75rem 1rem;
    border-radius: 20px;
  }
  
  @media (max-width: 480px) {
    padding: 0.6rem 0.8rem;
    border-radius: 18px;
  }
`;

const SearchIcon = styled(Search)`
  color: rgba(255, 255, 255, 0.6);
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  
  @media (max-width: 768px) {
    width: 22px;
    height: 22px;
  }
  
  @media (max-width: 480px) {
    width: 18px;
    height: 18px;
  }
`;

const SearchInput = styled.input`
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  color: white;
  font-size: 0.9rem;
  padding: 0 0.5rem;
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.6);
  }
  
  @media (max-width: 768px) {
    font-size: 1rem;
    padding: 0 0.75rem;
    
    &::placeholder {
      font-size: 0.95rem;
    }
  }
  
  @media (max-width: 480px) {
    font-size: 0.9rem;
    padding: 0 0.6rem;
    
    &::placeholder {
      font-size: 0.85rem;
    }
  }
`;

const ClearButton = styled.button`
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.6);
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: white;
  }
`;

const SuggestionsContainer = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: rgba(26, 26, 46, 0.95);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  margin-top: 0.5rem;
  max-height: 400px;
  overflow-y: auto;
  z-index: 1000;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  
  /* Scrollbar stylisée */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 255, 255, 0.3);
  }
  
  @media (max-width: 768px) {
    max-height: 300px;
    border-radius: 10px;
    margin-top: 0.75rem;
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.3);
  }
  
  @media (max-width: 480px) {
    max-height: 250px;
    border-radius: 8px;
    margin-top: 0.5rem;
    left: -0.5rem;
    right: -0.5rem;
  }
`;

const SuggestionItem = styled.div`
  padding: 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 1rem;
  
  &:hover {
    background: rgba(255, 255, 255, 0.05);
  }
  
  &:last-child {
    border-bottom: none;
  }
  
  @media (max-width: 768px) {
    padding: 0.875rem;
    gap: 0.875rem;
  }
  
  @media (max-width: 480px) {
    padding: 0.75rem;
    gap: 0.75rem;
  }
`;

const ProductImage = styled.img`
  width: 50px;
  height: 50px;
  object-fit: cover;
  border-radius: 8px;
  background: linear-gradient(135deg, #3b82f6, #9333ea);
  
  @media (max-width: 768px) {
    width: 45px;
    height: 45px;
    border-radius: 6px;
  }
  
  @media (max-width: 480px) {
    width: 40px;
    height: 40px;
    border-radius: 6px;
  }
`;

const ProductInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const ProductName = styled.div`
  font-weight: 600;
  color: white;
  font-size: 0.9rem;
  margin-bottom: 0.25rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  
  @media (max-width: 768px) {
    font-size: 0.95rem;
  }
  
  @media (max-width: 480px) {
    font-size: 0.85rem;
  }
`;

const ProductPrice = styled.div`
  color: #3b82f6;
  font-weight: 700;
  font-size: 0.85rem;
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
  
  @media (max-width: 480px) {
    font-size: 0.8rem;
  }
`;

const ProductSpecs = styled.div`
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.75rem;
  margin-top: 0.25rem;
`;

const NoResults = styled.div`
  padding: 2rem;
  text-align: center;
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.9rem;
`;

const LoadingContainer = styled.div`
  padding: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  color: rgba(255, 255, 255, 0.6);
`;

const LoadingSpinner = styled(Loader)`
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const SearchSuggestions = ({ placeholder = "Rechercher un produit..." }) => {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [allProducts, setAllProducts] = useState([]);
  const [isExpanded, setIsExpanded] = useState(false);
  const navigate = useNavigate();
  const searchRef = useRef(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  // Charger tous les produits au démarrage
  useEffect(() => {
    const loadProducts = async () => {
      try {
        const products = await productService.getProducts();
        setAllProducts(products);
      } catch (error) {
        console.error('Error loading products:', error);
      }
    };
    
    loadProducts();
  }, []);

  // Filtrer les suggestions avec debounce
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (query.trim().length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    debounceRef.current = setTimeout(() => {
      filterSuggestions(query.trim());
    }, 300);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [query, allProducts]);

  const filterSuggestions = (searchQuery) => {
    setIsLoading(true);
    
    // Simuler un délai de chargement pour l'UX
    setTimeout(() => {
      const filtered = allProducts.filter(product => 
        product.name && product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        product.description && typeof product.description === 'string' && product.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        product.category && typeof product.category === 'string' && product.category.toLowerCase().includes(searchQuery.toLowerCase()) ||
        product.category_name && typeof product.category_name === 'string' && product.category_name.toLowerCase().includes(searchQuery.toLowerCase())
      ).slice(0, 8); // Limiter à 8 résultats
      
      setSuggestions(filtered);
      setShowSuggestions(true);
      setIsLoading(false);
    }, 200);
  };

  const handleInputChange = (e) => {
    setQuery(e.target.value);
  };

  const handleClear = () => {
    setQuery('');
    setSuggestions([]);
    setShowSuggestions(false);
  };

  const handleSuggestionClick = (product) => {
    setQuery(product.name);
    setShowSuggestions(false);
    navigate(`/product/${product.id}`);
  };

  const handleExpand = () => {
    setIsExpanded(true);
    setTimeout(() => {
      inputRef.current?.focus();
    }, 100);
  };

  const handleCollapse = () => {
    if (!query.trim()) {
      setIsExpanded(false);
      setShowSuggestions(false);
    }
  };

  const handleInputFocus = () => {
    setShowSuggestions(true);
  };

  const handleOutsideClick = (e) => {
    if (searchRef.current && !searchRef.current.contains(e.target)) {
      setShowSuggestions(false);
      if (!query.trim()) {
        setIsExpanded(false);
      }
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  // Cliquer en dehors pour fermer les suggestions
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR').format(price) + ' FCFA';
  };

  return (
    <SearchContainer ref={searchRef} $isExpanded={isExpanded}>
      {!isExpanded ? (
        <CompactSearchButton onClick={handleExpand}>
          <Search size={20} />
        </CompactSearchButton>
      ) : (
        <SearchInputWrapper>
          <SearchIcon />
          <SearchInput
            ref={inputRef}
            type="text"
            value={query}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onBlur={handleCollapse}
            onFocus={handleInputFocus}
            placeholder={placeholder}
          />
          {query && (
            <ClearButton onClick={handleClear}>
              <X size={16} />
            </ClearButton>
          )}
        </SearchInputWrapper>
      )}

      {showSuggestions && (
        <SuggestionsContainer>
          {isLoading ? (
            <LoadingContainer>
              <LoadingSpinner size={16} />
              <span>Recherche...</span>
            </LoadingContainer>
          ) : suggestions.length > 0 ? (
            suggestions.map((product) => (
              <SuggestionItem
                key={product.id}
                onClick={() => handleSuggestionClick(product)}
              >
                <ProductImage
                  src={product.image ? `https://inza-backend.sajholding.org${product.image}` : '/api/placeholder/50/50'}
                  alt={product.name}
                  onError={(e) => {
                    e.target.src = '/api/placeholder/50/50';
                  }}
                />
                <ProductInfo>
                  <ProductName>{product.name}</ProductName>
                  <ProductPrice>{formatPrice(product.price)}</ProductPrice>
                  <ProductSpecs>
                    {product.storage && `${product.storage} • `}
                    {product.color && `${product.color}`}
                  </ProductSpecs>
                </ProductInfo>
              </SuggestionItem>
            ))
          ) : query.trim().length >= 2 ? (
            <NoResults>
              Aucun produit trouvé pour "{query}"
            </NoResults>
          ) : null}
        </SuggestionsContainer>
      )}
    </SearchContainer>
  );
};

export default SearchSuggestions;
